import type { CSSProperties, RefObject } from 'react'
import { Pencil } from 'lucide-react'
import DeltaBadge from './DeltaBadge'
import { EN_DASH } from './leagueDetailFormat'

interface LeagueDetailHeaderProps {
  leagueName: string
  motto?: string | null
  // Only the league owner with a save handler wired gets the add/edit affordances.
  canEditMotto: boolean
  metaLine: string
  yourRank: number
  yourPoints: number
  rankDelta: number | null
  yourGwPoints?: number | null
  isSeasonOpener: boolean
  onEditMotto: () => void
  // Exposes the header name node so the shared-element flight can measure its destination rect.
  // Absent when the route was entered without a transition.
  nameRef?: (el: HTMLElement | null) => void
  // The hero rank block drives the sticky-header collapse threshold.
  heroRankRef: RefObject<HTMLDivElement | null>
}

function LeagueDetailHeader({
  leagueName,
  motto,
  canEditMotto,
  metaLine,
  yourRank,
  yourPoints,
  rankDelta,
  yourGwPoints,
  isSeasonOpener,
  onEditMotto,
  nameRef,
  heroRankRef,
}: LeagueDetailHeaderProps) {
  return (
    <header className="league-detail__header">
      <h1 className="league-detail__name" ref={nameRef}>
        {leagueName}
      </h1>

      {/* Motto slot. When a motto is set — or the viewer is the admin, who gets a
          `Say something` prompt — the slot reserves two lines so a one-line motto doesn't shift the
          meta below it. A non-admin with no motto sees nothing and the slot closes up entirely. */}
      {(motto || canEditMotto) && (
        <div
          className="league-detail__motto-slot league-detail__enter-item"
          style={{ '--enter-order': 0 } as CSSProperties}
        >
          {motto ? (
            canEditMotto ? (
              <button
                type="button"
                className="league-detail__motto league-detail__motto--editable"
                onClick={onEditMotto}
              >
                <span className="league-detail__motto-text">{motto}</span>
              </button>
            ) : (
              <p className="league-detail__motto">
                <span className="league-detail__motto-text">{motto}</span>
              </p>
            )
          ) : (
            <button
              type="button"
              className="league-detail__motto-add"
              onClick={onEditMotto}
            >
              <Pencil size={13} aria-hidden="true" />
              Say something
            </button>
          )}
        </div>
      )}

      <p
        className="league-detail__meta league-detail__enter-item"
        style={{ '--enter-order': 1 } as CSSProperties}
      >
        {metaLine}
      </p>

      {/* Hero row: rank and points sit in their own band, below the name — the name must not
          compete with the rank in the same horizontal band. */}
      <div
        className="league-detail__hero league-detail__enter-item"
        style={{ '--enter-order': 2 } as CSSProperties}
      >
        <div className="league-detail__hero-block" ref={heroRankRef}>
          <p className="league-detail__hero-label label-eyebrow">Your rank</p>
          <div className="league-detail__hero-value-row">
            <span className="league-detail__hero-rank">
              {isSeasonOpener ? EN_DASH : yourRank.toLocaleString()}
            </span>
            {!isSeasonOpener && (
              <DeltaBadge
                delta={rankDelta}
                baseClass="league-detail__hero-delta"
              />
            )}
          </div>
        </div>

        <span className="league-detail__hero-rule" aria-hidden="true" />

        <div className="league-detail__hero-block">
          <p className="league-detail__hero-label label-eyebrow">Points</p>
          <div className="league-detail__hero-value-row">
            <span className="league-detail__hero-points">
              {isSeasonOpener ? EN_DASH : yourPoints.toLocaleString()}
            </span>
            {!isSeasonOpener && yourGwPoints != null && (
              <span className="league-detail__hero-gw">{`+${yourGwPoints}`}</span>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default LeagueDetailHeader
